import { all, call, put, select } from 'redux-saga/effects';
import {
  PapiStudioPagesStash,
  PapiRequestDaSharedGallectionFolders,
  PapiDaSharedGallectionFolders,
} from '@wix/da-papi-types';
import { getNextOffset } from '@wix/da-shared-react/pkg/Stream/withOffset/selectors';
import { fetch } from '@wix/da-shared-react/pkg/Stream/withOffset/actions';
import {
  takeStreamFetch,
  fetchStreamContents,
} from '@wix/da-shared-react/pkg/Stream/helpers';
import { addNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { NotificationType } from '@wix/da-shared-react/pkg/Notifications';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import { normalizeDeviations } from '@wix/da-shared-react/pkg/redux/normalizr/helpers';
import {
  getItemsPerFetch,
  getStreamParam,
} from '@wix/da-shared-react/pkg/Stream/base/selectors';
import { fetchMoreDeviations, fetchMoreSubfolders } from '../actions/stash';
import { STASH_FOLDER_DEVIATIONS_STREAM } from '../../constants';
import { getStashFolder } from '../selectors/stash';
import { FolderType } from '../types/folders';

function* fetchDeviationsOffset(offset: number) {
  const { streamId } = STASH_FOLDER_DEVIATIONS_STREAM;
  const folder: ReturnType<typeof getStashFolder> =
    yield select(getStashFolder);
  const params = {
    init: false,
    deviations_offset: offset,
    folderid: folder!.folderId,
  };

  yield fetchStreamContents<any, any>({
    streamId,
    url: '/studio/pages/stash',
    papiProject: 'v1',
    params,
    onSuccess: function* (response: PapiStudioPagesStash) {
      const { studioResults = [] } = response.deviations;
      const deviations = studioResults.map(({ deviation }) => deviation);

      if (deviations.length > 0) {
        const { entities } = normalizeDeviations(deviations);
        yield put(putEntities({ entities }));
      }
    },
    onFailure: function* (papiError) {
      yield put(
        addNotification(NotificationType.Generic, {
          messageLocaleKey: 'studio.notifications.refreshTable.error',
        })
      );
    },
    getStreamData: (response: any) => {
      const { hasMore, nextOffset, pageSize } = response.deviations;

      /**
       * This can happen in some edge cases around users manually manipulating the stash folder we save
       * these draft submissions in.
       */
      if (!response.deviations.studioResults) {
        return {
          hasMore: false,
          items: [],
        };
      }

      const results = response.deviations.studioResults.map(r => r.deviation);
      const { result: normalizedDeviationIds } = normalizeDeviations(results);
      return {
        hasMore: !!hasMore,
        items: normalizedDeviationIds,
        itemsPerFetch: pageSize,
        nextOffset,
      };
    },
  });
}

function* handleFetchMoreDeviations() {
  yield put(fetch(STASH_FOLDER_DEVIATIONS_STREAM.streamId)); // set loading state on stream
  yield fetchDeviationsOffset(
    yield select(getNextOffset, STASH_FOLDER_DEVIATIONS_STREAM.streamId)
  );
}

export function* fetchFoldersStream(params: {
  type: FolderType;
  streamId: string;
  offset: number;
  limit: number;
  username: string;
  folderId?: number;
  reset?: boolean;
}) {
  yield put(fetch(params.streamId)); // set loading state on stream
  yield fetchStreamContents<
    PapiRequestDaSharedGallectionFolders,
    PapiDaSharedGallectionFolders
  >({
    streamId: params.streamId,
    params: {
      type: params.type,
      username: params.username,
      folderid: params.folderId,
      offset: params.offset,
      limit: params.limit,
    },
    url: 'gallection/folders',
    getStreamData: result => ({
      hasMore: !!result.hasMore,
      items: result.results,
      nextOffset: result.nextOffset ?? undefined,
      reset: params.reset,
    }),
  });
}

function* handleFetchMoreSubfolders({
  payload: { streamId },
}: ReturnType<typeof fetchMoreSubfolders>) {
  const { offset, limit, folderId, username } = yield select(state => ({
    offset: getNextOffset(state, streamId),
    limit: getItemsPerFetch(state, streamId),
    folderId: getStreamParam(state, streamId, 'folderId'),
    username: getStreamParam(state, streamId, 'username'),
  }));

  yield call(fetchFoldersStream, {
    type: 'stash',
    streamId,
    folderId,
    username,
    offset,
    limit,
  });
}

export default function* stashSaga() {
  yield all([
    takeStreamFetch(fetchMoreDeviations, handleFetchMoreDeviations),
    takeStreamFetch(fetchMoreSubfolders, handleFetchMoreSubfolders),
  ]);
}
